import { phone } from 'phone';

import { acceptedAudioFileExtensions, MAX_UPLOAD_SIZE_BYTES } from './media';
import { NAME_REGEX, EMAIL_REGEX, URL_REGEX } from './regex';
import { normalizePersonName } from './string';

/** Checkbox / RHF edge cases: duplicate `name` -> ['on','on']; native value "on". */
export function coerceCheckboxBoolean(value: unknown): boolean {
  if (value === true) return true;
  if (value === false || value == null) return false;
  if (Array.isArray(value)) return value.some((v) => v === true || v === 'on');
  if (value === 'on') return true;
  return Boolean(value);
}

export const MIN_NAME_LENGTH = 2;
export const MAX_NAME_LENGTH = 50;

// Removes trailing spaces
export const trimValue = (str: string) => str.trim();

export const providerNameRegisterOptions = {
  required: { value: true, message: 'Name is required' },
  setValueAs: normalizePersonName,
  pattern: {
    value: NAME_REGEX,
    message: 'Invalid organization name',
  },
  minLength: {
    value: MIN_NAME_LENGTH,
    message: `Name must be at least ${MIN_NAME_LENGTH} characters`,
  },
  maxLength: {
    value: MAX_NAME_LENGTH,
    message: `Name must be less than ${MAX_NAME_LENGTH} characters`,
  },
};

export const fullNameRegisterOptions = {
  required: { value: true, message: 'Name is required' },
  setValueAs: normalizePersonName,
  pattern: {
    value: NAME_REGEX,
    message: 'Invalid full name',
  },
  minLength: {
    value: MIN_NAME_LENGTH,
    message: `Name must be at least ${MIN_NAME_LENGTH} characters`,
  },
  maxLength: {
    value: MAX_NAME_LENGTH,
    message: `Name must be less than ${MAX_NAME_LENGTH} characters`,
  },
};

export const emailRegisterOptions = {
  required: { value: true, message: 'Email is required' },
  pattern: {
    value: EMAIL_REGEX,
    message: 'Invalid email',
  },
  setValueAs: trimValue,
};

export const linkRegisterOptions = {
  pattern: {
    value: URL_REGEX,
    message: 'Invalid link',
  },
};

/** Invisible / bidi / zero-width marks often introduced by paste from Contacts, WhatsApp, etc. */
const PHONE_INVISIBLE_CHARS_REGEX =
  /[\u200B-\u200D\u2060\uFEFF\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;

/** Plus-like unicode characters that look like "+" but fail `phone`'s /^\+/ check. */
const PHONE_PLUS_LOOKALIKES_REGEX = /[\uFF0B\u2795\u207A\u208A]/g;

/**
 * Strip paste artifacts and normalize lookalike "+" so `phone()` can parse the number.
 * Also accepts a country code without "+" (e.g. "1 (289) 219-4531" → "+12892194531").
 */
export function normalizePhoneNumber(value: string): {
  isValid: boolean;
  phoneNumber: string | null;
  sanitized: string;
} {
  const sanitized = value
    .trim()
    .replace(PHONE_INVISIBLE_CHARS_REGEX, '')
    .replace(/^tel:/i, '')
    .replace(PHONE_PLUS_LOOKALIKES_REGEX, '+')
    .trim();

  if (!sanitized) {
    return { isValid: false, phoneNumber: null, sanitized };
  }

  let result = phone(sanitized);
  if (result.isValid) {
    return {
      isValid: true,
      phoneNumber: result.phoneNumber,
      sanitized,
    };
  }

  // "1 (289) 219-4531" fails without "+", but "+12892194531" is valid.
  const digits = sanitized.replace(/\D/g, '');
  if (!sanitized.includes('+') && digits.length >= 11) {
    result = phone(`+${digits}`);
    if (result.isValid) {
      return {
        isValid: true,
        phoneNumber: result.phoneNumber,
        sanitized,
      };
    }
  }

  return { isValid: false, phoneNumber: null, sanitized };
}

export const phoneRegisterOptions = {
  setValueAs: (phoneNumber: string) => {
    const result = normalizePhoneNumber(phoneNumber);
    if (!result.isValid) return result.sanitized;
    return result.phoneNumber as string;
  },
  validate: (phoneNumber: string | undefined) => {
    if (!phoneNumber) return;
    const { isValid } = normalizePhoneNumber(phoneNumber);

    if (!isValid) {
      return 'Unrecognized phone number. Try international format, e.g. +1 289 219 4531.';
    }
  },
};

export const audioFileRegisterOptions = {
  required: 'Please select a file to upload',
  validate: (fileList: FileList | undefined) => {
    if (fileList) {
      const audioFile = fileList?.[0];
      if (audioFile.size > MAX_UPLOAD_SIZE_BYTES) {
        return "That file is a bit too big. Try one that's smaller than 100 MB";
      } else if (!acceptedAudioFileExtensions.includes(audioFile.type)) {
        return 'Please upload an MP3 or M4A file';
      }
    }
  },
};
